/**
 * Agent output panel: bottom panel with logs, streaming text, pipeline tabs,
 * historical job tabs, resizable panel, dark mode styling, ephemeral entries.
 *
 * Extracted from UpgradeMedicPage.tsx — preserves all existing behavior.
 */
import React from 'react';
import { Badge } from '@amzn/awsui-components-core';
import { AgentLogEntry } from '../types';
import { PersistedEphemeralEntry } from '../hooks/useAgentPanel';
import styles from '../styles.module.scss';

interface AgentPanelContainerProps {
  isDarkMode: boolean;
  // Panel state
  panelHeight: number;
  setPanelHeight: (h: number) => void;
  setIsResizing: (r: boolean) => void;
  setIsAgentPanelVisible: (v: boolean) => void;
  // Status
  jobStatus: string;
  progressMessage: string;
  // Logs
  filteredAgentLogs: AgentLogEntry[];
  // Streaming
  streamingText: string;
  // Ephemeral entries
  persistedEphemeralEntries: PersistedEphemeralEntry[];
  setPersistedEphemeralEntries: React.Dispatch<React.SetStateAction<PersistedEphemeralEntry[]>>;
  pipelinePersistedEphemeral: Record<string, PersistedEphemeralEntry[]>;
  setPipelinePersistedEphemeral: React.Dispatch<React.SetStateAction<Record<string, PersistedEphemeralEntry[]>>>;
  // Tabs
  activePipelineTab: string;
  setActivePipelineTab: (tab: string) => void;
  parallelPipelines: string[];
  setParallelPipelines: React.Dispatch<React.SetStateAction<string[]>>;
  agentLogs: AgentLogEntry[];
  pipelineLogs: Record<string, AgentLogEntry[]>;
  setPipelineLogs: React.Dispatch<React.SetStateAction<Record<string, AgentLogEntry[]>>>;
  // Historical
  historicalJobTabs: Array<{ jobId: string; title: string }>;
  setHistoricalJobTabs: React.Dispatch<React.SetStateAction<Array<{ jobId: string; title: string }>>>;
  setHistoricalJobLogs: React.Dispatch<React.SetStateAction<Record<string, AgentLogEntry[]>>>;
  // Scroll
  logsContainerRef: React.RefObject<HTMLDivElement>;
  handleLogsScroll: () => void;
  // CloudWatch
  isAutoRefreshing: boolean;
  cloudWatchPollingJobId: string | null;
}

export const AgentPanelContainer: React.FC<AgentPanelContainerProps> = ({
  isDarkMode,
  panelHeight,
  setPanelHeight,
  setIsResizing,
  setIsAgentPanelVisible,
  jobStatus,
  progressMessage,
  filteredAgentLogs,
  streamingText,
  persistedEphemeralEntries,
  setPersistedEphemeralEntries,
  pipelinePersistedEphemeral,
  setPipelinePersistedEphemeral,
  activePipelineTab,
  setActivePipelineTab,
  parallelPipelines,
  setParallelPipelines,
  agentLogs,
  pipelineLogs,
  setPipelineLogs,
  historicalJobTabs,
  setHistoricalJobTabs,
  setHistoricalJobLogs,
  logsContainerRef,
  handleLogsScroll,
  isAutoRefreshing,
  cloudWatchPollingJobId
}) => {
  // Cloudscape-aligned color theme for dark and light modes
  const theme = isDarkMode
    ? {
        panelBg: '#0f172a',
        panelShadow: '0 -4px 16px rgba(0, 0, 0, 0.4)',
        resizeGradient: 'linear-gradient(to bottom, #475569, #334155)',
        headerBg: '#1e293b',
        headerBorder: 'rgba(255, 255, 255, 0.1)',
        titleColor: '#ffffff',
        controlBorder: 'rgba(255, 255, 255, 0.2)',
        controlColor: 'rgba(255, 255, 255, 0.7)',
        progressBg: 'rgba(0, 100, 200, 0.15)',
        progressColor: '#60a5fa',
        autoRefreshBg: 'rgba(34, 197, 94, 0.2)',
        autoRefreshColor: '#4ade80',
        logsBg: '#0f172a',
        timestampColor: 'rgba(255, 255, 255, 0.4)',
        defaultTextColor: 'rgba(255, 255, 255, 0.85)',
        ephemeralBg: 'rgba(99, 102, 241, 0.1)',
        ephemeralLabelColor: 'rgba(147, 197, 253, 0.9)',
        ephemeralTextColor: 'rgba(199, 210, 254, 0.9)',
        ephemeralCollapsedColor: 'rgba(147, 197, 253, 0.7)',
        streamingBg: 'rgba(99, 102, 241, 0.15)',
        streamingTextColor: 'rgba(199, 210, 254, 0.9)',
        loadingColor: 'rgba(255, 255, 255, 0.5)',
        // Log level colors
        infoColor: '#4ade80',
        warningColor: '#fbbf24',
        errorColor: '#f87171',
        phaseColor: '#4ade80',
        toolColor: '#60a5fa',
        // Log level backgrounds
        warningBg: 'rgba(217, 119, 6, 0.15)',
        errorBg: 'rgba(220, 38, 38, 0.15)',
        phaseBg: 'rgba(6, 125, 98, 0.15)',
        toolBg: 'rgba(0, 115, 187, 0.15)'
      }
    : {
        panelBg: '#fafafa',
        panelShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
        resizeGradient: 'linear-gradient(to bottom, #d1d5db, #e5e7eb)',
        headerBg: '#f0f0f0',
        headerBorder: '#d5d5d5',
        titleColor: '#0f1b2d',
        controlBorder: 'rgba(0, 0, 0, 0.2)',
        controlColor: 'rgba(0, 0, 0, 0.6)',
        progressBg: '#e8f4fd',
        progressColor: '#0073bb',
        autoRefreshBg: 'rgba(34, 197, 94, 0.12)',
        autoRefreshColor: '#16a34a',
        logsBg: '#fafafa',
        timestampColor: '#687078',
        defaultTextColor: '#0f1b2d',
        ephemeralBg: 'rgba(99, 102, 241, 0.06)',
        ephemeralLabelColor: '#6366f1',
        ephemeralTextColor: '#4338ca',
        ephemeralCollapsedColor: '#6366f1',
        streamingBg: 'rgba(99, 102, 241, 0.08)',
        streamingTextColor: '#4338ca',
        loadingColor: '#687078',
        // Log level colors
        infoColor: '#037f0c',
        warningColor: '#8a6d13',
        errorColor: '#d13212',
        phaseColor: '#037f0c',
        toolColor: '#0073bb',
        // Log level backgrounds
        warningBg: 'rgba(217, 119, 6, 0.08)',
        errorBg: 'rgba(220, 38, 38, 0.08)',
        phaseBg: 'rgba(6, 125, 98, 0.08)',
        toolBg: 'rgba(0, 115, 187, 0.08)'
      };
  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startY = e.clientY;
    const startHeight = panelHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = startY - moveEvent.clientY;
      const newHeight = Math.min(Math.max(startHeight + deltaY, 100), window.innerHeight - 100);
      setPanelHeight(newHeight);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const displayPersistedEntries = activePipelineTab.startsWith('history-')
    ? []
    : pipelinePersistedEphemeral[activePipelineTab] || persistedEphemeralEntries;

  type UnifiedEntry = { type: 'log'; data: AgentLogEntry } | { type: 'ephemeral'; data: PersistedEphemeralEntry };

  const unifiedList: UnifiedEntry[] = [
    ...filteredAgentLogs.map((log) => ({ type: 'log' as const, data: log })),
    ...displayPersistedEntries.map((entry) => ({ type: 'ephemeral' as const, data: entry }))
  ];
  unifiedList.sort((a, b) => a.data.timestamp.getTime() - b.data.timestamp.getTime());

  const getLogStyle = (log: AgentLogEntry): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {};
    if (log.level === 'warning') {
      baseStyle.backgroundColor = theme.warningBg;
      baseStyle.borderLeft = '3px solid #f59e0b';
    } else if (log.level === 'error') {
      baseStyle.backgroundColor = theme.errorBg;
      baseStyle.borderLeft = '3px solid #ef4444';
    } else if (log.level === 'phase') {
      baseStyle.backgroundColor = theme.phaseBg;
      baseStyle.borderLeft = '3px solid #10b981';
    } else if (log.level === 'tool') {
      baseStyle.backgroundColor = theme.toolBg;
      baseStyle.borderLeft = '3px solid #3b82f6';
    }
    return baseStyle;
  };

  const getMessageColor = (log: AgentLogEntry): string => {
    if (log.level === 'info') return theme.infoColor;
    if (log.level === 'warning') return theme.warningColor;
    if (log.level === 'error') return theme.errorColor;
    if (log.level === 'phase') return theme.phaseColor;
    if (log.level === 'tool') return theme.toolColor;
    return theme.defaultTextColor;
  };

  const toggleEphemeralExpand = (entryId: number) => {
    setPipelinePersistedEphemeral((prev) => ({
      ...prev,
      [activePipelineTab]: (prev[activePipelineTab] || []).map((e) => (e.id === entryId ? { ...e, isExpanded: !e.isExpanded } : e))
    }));
  };

  const closePipelineTab = (pipelineId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setParallelPipelines((prev) => prev.filter((p) => p !== pipelineId));
    setPipelineLogs((prev) => {
      const updated = { ...prev };
      delete updated[pipelineId];
      return updated;
    });
    if (activePipelineTab === pipelineId) {
      // Switch to first remaining pipeline tab, or first historical tab
      const remaining = parallelPipelines.filter((p) => p !== pipelineId);
      if (remaining.length > 0) {
        setActivePipelineTab(remaining[0]);
      } else if (historicalJobTabs.length > 0) {
        setActivePipelineTab(`history-${historicalJobTabs[0].jobId}`);
      }
    }
  };

  const closeHistoricalTab = (tabJobId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setHistoricalJobTabs((prev) => prev.filter((t) => t.jobId !== tabJobId));
    setHistoricalJobLogs((prev) => {
      const updated = { ...prev };
      delete updated[tabJobId];
      return updated;
    });
    if (activePipelineTab === `history-${tabJobId}`) {
      // Switch to first pipeline tab, or first remaining historical tab
      if (parallelPipelines.length > 0) {
        setActivePipelineTab(parallelPipelines[0]);
      } else {
        const remaining = historicalJobTabs.filter((t) => t.jobId !== tabJobId);
        if (remaining.length > 0) {
          setActivePipelineTab(`history-${remaining[0].jobId}`);
        }
      }
    }
  };

  return (
    <div
      className={styles.agentPanel}
      style={{
        height: panelHeight,
        backgroundColor: theme.panelBg,
        boxShadow: theme.panelShadow
      }}
    >
      {/* Resize handle */}
      <div className={styles.agentPanelResize} style={{ background: theme.resizeGradient }} onMouseDown={handleResizeMouseDown} />

      {/* Header */}
      <div
        className={styles.agentPanelHeader}
        style={{
          backgroundColor: theme.headerBg,
          borderBottomColor: theme.headerBorder
        }}
      >
        <div className={styles.agentPanelTitle} style={{ color: theme.titleColor }}>
          <span>🤖 Agent Output</span>
          <Badge color={jobStatus === 'running' ? 'green' : jobStatus === 'failed' ? 'red' : 'grey'}>{jobStatus}</Badge>
        </div>
        <div className={styles.agentPanelControls}>
          <button
            onClick={() => setIsAgentPanelVisible(false)}
            title="Minimize console"
            style={{
              borderColor: theme.controlBorder,
              color: theme.controlColor
            }}
          >
            ▼
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div
        className={styles.agentPanelProgress}
        style={{
          backgroundColor: theme.progressBg,
          color: theme.progressColor
        }}
      >
        {(jobStatus === 'running' || jobStatus === 'connecting') && <span className={styles.loadingSpinner}>⏳</span>}
        {progressMessage}
        {isAutoRefreshing && cloudWatchPollingJobId && (
          <span
            style={{
              marginLeft: '12px',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              backgroundColor: theme.autoRefreshBg,
              color: theme.autoRefreshColor,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span style={{ animation: 'pulse 2s infinite' }}>🔄</span>
            Auto-refreshing
          </span>
        )}
      </div>

      {/* Pipeline tabs */}
      {(parallelPipelines.length >= 1 || historicalJobTabs.length > 0) && (
        <div className={`${styles.pipelineTabs} ${isDarkMode ? styles.pipelineTabsDark : ''}`}>
          {parallelPipelines.map((pipelineId) => (
            <button
              key={pipelineId}
              className={activePipelineTab === pipelineId ? styles.activeTab : ''}
              onClick={() => setActivePipelineTab(pipelineId)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <span>
                {pipelineId} ({pipelineLogs[pipelineId]?.length || 0})
              </span>
              <span
                onClick={(e) => closePipelineTab(pipelineId, e)}
                style={{ cursor: 'pointer', fontSize: '10px', padding: '2px 4px', borderRadius: '2px', marginLeft: '4px' }}
                title="Close tab"
              >
                ✕
              </span>
            </button>
          ))}
          {historicalJobTabs.map((tab) => (
            <button
              key={`history-${tab.jobId}`}
              className={activePipelineTab === `history-${tab.jobId}` ? styles.activeTab : ''}
              onClick={() => setActivePipelineTab(`history-${tab.jobId}`)}
              style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              <span>📜 {tab.title}</span>
              <span
                onClick={(e) => closeHistoricalTab(tab.jobId, e)}
                style={{ cursor: 'pointer', fontSize: '10px', padding: '2px 4px', borderRadius: '2px', marginLeft: '4px' }}
                title="Close tab"
              >
                ✕
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Log entries */}
      <div
        className={styles.agentPanelLogs}
        ref={logsContainerRef}
        key={`logs-${activePipelineTab}`}
        onScroll={handleLogsScroll}
        style={{ backgroundColor: theme.logsBg }}
      >
        <div style={{ width: '100%' }}>
          {unifiedList.map((item) => {
            if (item.type === 'log') {
              const log = item.data;
              return (
                <div key={`log-${log.id}`} className={styles.logEntry} style={getLogStyle(log)}>
                  <span className={styles.logTime} style={{ color: theme.timestampColor }}>
                    {log.timestamp.toLocaleTimeString()}
                  </span>
                  <span className={styles.logMessage} style={{ color: getMessageColor(log) }}>
                    {log.message}
                  </span>
                </div>
              );
            }

            const entry = item.data;
            const truncatedText = entry.text.length > 100 && !entry.isExpanded ? entry.text.substring(0, 100) + '...' : entry.text;

            return (
              <div
                key={`ephemeral-${entry.id}`}
                className={styles.logEntry}
                style={{
                  backgroundColor: theme.ephemeralBg,
                  borderLeft: '3px solid #6366f1',
                  cursor: 'pointer',
                  whiteSpace: entry.isExpanded ? 'pre-wrap' : 'normal',
                  flexDirection: entry.isExpanded ? 'column' : 'row',
                  alignItems: entry.isExpanded ? 'flex-start' : 'center',
                  overflow: 'hidden'
                }}
                onClick={() => toggleEphemeralExpand(entry.id)}
                title={entry.isExpanded ? 'Click to collapse' : 'Click to expand'}
              >
                {entry.isExpanded ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span className={styles.logTime} style={{ color: theme.timestampColor }}>
                        {entry.timestamp.toLocaleTimeString()}
                      </span>
                      <span style={{ color: theme.ephemeralLabelColor, fontSize: '11px' }}>🤖 Agent thinking</span>
                    </div>
                    <span className={styles.logMessage} style={{ color: theme.ephemeralTextColor }}>
                      {entry.text}
                    </span>
                  </>
                ) : (
                  <>
                    <span className={styles.logTime} style={{ color: theme.timestampColor }}>
                      {entry.timestamp.toLocaleTimeString()}
                    </span>
                    <span className={styles.logMessage} style={{ color: theme.ephemeralCollapsedColor }}>
                      🤖 {truncatedText}
                    </span>
                  </>
                )}
              </div>
            );
          })}

          {/* Live streaming text */}
          {streamingText && (
            <div
              className={styles.logEntry}
              style={{
                backgroundColor: theme.streamingBg,
                borderLeft: '3px solid #818cf8',
                whiteSpace: 'pre-wrap'
              }}
            >
              <span className={styles.logTime} style={{ color: theme.timestampColor }}>
                {new Date().toLocaleTimeString()}
              </span>
              <span className={styles.logMessage} style={{ color: theme.streamingTextColor }}>
                🤖 {streamingText}
                <span className={styles.loadingSpinner}>▊</span>
              </span>
            </div>
          )}

          {/* Bottom loading indicator — visible while job is active or CloudWatch is polling */}
          {(jobStatus === 'running' || jobStatus === 'connecting' || (isAutoRefreshing && cloudWatchPollingJobId)) && !streamingText && (
            <div
              className={styles.loadingIndicator}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: theme.loadingColor
              }}
            >
              <span className={styles.loadingSpinner}>⏳</span>
              <span>
                {jobStatus === 'connecting' ? 'Connecting' : 'Processing'}
                <span className={styles.loadingDots} />
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
